<template>
  <section class="shop-by-concern">
    <h2 class="section-title">Shop By Concern</h2>

    <div
      ref="viewport"
      class="slider-viewport"
      :class="{ dragging: isDragging }"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
      @scroll="wrap"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div ref="track" class="slider-track">
        <router-link
          v-for="(concern, i) in loopConcerns"
          :key="`concern-${i}`"
          class="concern-slide"
          :style="slideStyle"
          :to="{ path: '/category', query: { c: concern.slug } }"
          @click="onItemClick"
        >
          <div class="concern-card">
            <div class="concern-image-wrap">
              <img
                :src="`${BASE_IMAGE_PREFIX}/${concern.image}`"
                :alt="concern.label"
                class="concern-image"
                draggable="false"
              />
            </div>
            <div class="concern-label">{{ concern.label }}</div>
          </div>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { BASE_IMAGE_PREFIX } from "@/globals";

export default {
  name: "ShopByConcern",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      visibleCount: 5,
      slideWidth: 0,
      setWidth: 0,
      hovering: false,
      isDragging: false,
      pointerActive: false,
      dragMoved: false,
      startX: 0,
      startScroll: 0,
      rafId: null,
      speed: 0.6,
      concerns: [
        { label: "Acne", slug: "acne", image: "acne.jpg" },
        { label: "Dullness", slug: "dullness", image: "dullness.jpg" },
        { label: "Dryness", slug: "dryness", image: "dryness.jpg" },
        { label: "Uneven skintone", slug: "uneven-skintone", image: "uneven-skintone.jpg" },
        { label: "Blackheads", slug: "blackheads", image: "blackheads.jpg" },
        { label: "Hyperpigmentation", slug: "hyperpigmentation", image: "hyperpigmentation.jpg" },
      ],
    };
  },
  computed: {
    // Duplicate the list so the slider can loop seamlessly in both directions.
    loopConcerns() {
      return [...this.concerns, ...this.concerns];
    },
    slideStyle() {
      return this.slideWidth ? { width: `${this.slideWidth}px` } : {};
    },
  },
  mounted() {
    this.$nextTick(this.measure);
    window.addEventListener("resize", this.measure);
    this.rafId = requestAnimationFrame(this.step);
  },
  beforeUnmount() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.measure);
  },
  methods: {
    measure() {
      const vp = this.$refs.viewport;
      if (!vp) return;
      const width = window.innerWidth;
      if (width < 576) this.visibleCount = 2;
      else if (width < 992) this.visibleCount = 3;
      else if (width < 1200) this.visibleCount = 4;
      else this.visibleCount = 5;

      this.slideWidth = vp.clientWidth / this.visibleCount;
      this.setWidth = this.slideWidth * this.concerns.length;
    },
    step() {
      const vp = this.$refs.viewport;
      if (vp && !this.hovering && !this.pointerActive) {
        vp.scrollLeft += this.speed;
        this.wrap();
      }
      this.rafId = requestAnimationFrame(this.step);
    },
    wrap() {
      const vp = this.$refs.viewport;
      if (!vp || !this.setWidth) return;
      if (vp.scrollLeft >= this.setWidth) {
        vp.scrollLeft -= this.setWidth;
      } else if (vp.scrollLeft <= 0) {
        vp.scrollLeft += this.setWidth;
      }
    },
    onPointerDown(e) {
      this.pointerActive = true;
      this.dragMoved = false;
      this.startX = e.clientX;
      this.startScroll = this.$refs.viewport.scrollLeft;
      // Only mouse gets JS drag; touch uses native scrolling.
      if (e.pointerType === "mouse") {
        this.isDragging = true;
      }
    },
    onPointerMove(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.startX;
      if (Math.abs(delta) > 3) this.dragMoved = true;
      this.$refs.viewport.scrollLeft = this.startScroll - delta;
      this.wrap();
    },
    onPointerUp() {
      this.isDragging = false;
      this.pointerActive = false;
    },
    onItemClick(e) {
      // Prevent navigation if the pointer was dragging (not a real click).
      if (this.dragMoved) {
        e.preventDefault();
        this.dragMoved = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shop-by-concern {
  padding: 40px 0;

  .section-title {
    text-align: center;
    font-weight: 700;
    font-size: 2rem;
    color: #1e1e2d;
    margin-bottom: 2rem;
  }
}

.slider-viewport {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  cursor: grab;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.dragging {
    cursor: grabbing;
    user-select: none;
  }
}

.slider-track {
  display: flex;
  width: max-content;
}

.concern-slide {
  flex: 0 0 auto;
  box-sizing: border-box;
  padding: 0 8px;
  text-decoration: none;
  -webkit-user-drag: none;
}

.concern-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-4px);
  }
}

.concern-image-wrap {
  background: var(--light-pink);
  overflow: hidden;
}

.concern-image {
  width: 100%;
  // height: clamp(280px, 30vw, 440px);
  object-fit: cover;
  object-position: top center;
  display: block;
}

.concern-label {
  background: var(--secondary-color);
  color: #fff;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  padding: 14px 8px;
}
</style>
